import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';

const ProductShowcase = () => {
  const [products, setProducts] = useState([]);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const sectionRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, threshold: 0.1 });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/products`);
        const data = await response.json();
        if (data.success) {
          setProducts(data.data);
          setDisplayedProducts(data.data);
        } else {
          setError('Failed to fetch products');
        }
        
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('An error occurred while fetching products');
      }
    };
    fetchProducts();
  }, []);


const isHttpUrl = (s) => typeof s === 'string' && /^https?:\/\//i.test(s);

const getProductImage = (input) => {
  // allow string URL directly
  if (isHttpUrl(input)) return input;

  // allow array of URLs directly
  if (Array.isArray(input)) {
    const firstUrl = input.find(isHttpUrl);
    if (firstUrl) return firstUrl;
    return '/placeholder-product.jpg';
  }

  // normal product object
  const product = input || {};
  if (isHttpUrl(product.imageUrl)) return product.imageUrl;
  if (Array.isArray(product.images)) {
    const firstUrl = product.images.find(isHttpUrl);
    if (firstUrl) return firstUrl;
  }
  return '/placeholder-product.jpg';
};
// const getProductImage = (product) => {
//   if (product?.imageUrl) return product.imageUrl;         // new field (Blob URL)
//   if (product?.images?.length) return product.images[0];  // legacy/extra URLs
//   return '/placeholder-product.jpg';
// };

  // ⚠️ CHANGED: return a button instead of <a>, to avoid <a> inside <a>
  const formatPrice = (product) => {
    if (!product.price && product.price !== 0) return 'Price not available';

    if (product.showPrice === false) {
      const waHref = product.whatsappUrl || 'https://wa.me/9149490169';
      const onWhatsAppClick = (e) => {
        e.preventDefault();   // stop parent <Link> navigation
        e.stopPropagation();  // stop bubbling
        window.open(waHref, '_blank', 'noopener,noreferrer');
      };
      return (
        <button
          type="button"
          onClick={onWhatsAppClick}
          className="text-[#18181B] underline hover:text-[#a98028] transition-colors duration-200"
        >
          Request for price
        </button>
      );
    }

    return `₹${product.price}`;
  };

  const onImgError = (e) => {
    // avoid infinite error loops
    e.currentTarget.onerror = null;
    e.currentTarget.src = '/placeholder-product.jpg';
  };

  // Framer Motion animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const titleVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const lineVariants = {
    hidden: { opacity: 0, scaleX: 0 },
    visible: {
      opacity: 1,
      scaleX: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
        delay: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: (index) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
        delay: 0.3 + (index * 0.1)
      }
    })
  };

  const imageVariants = {
    hidden: { scale: 1 },
    hover: { 
      scale: 1.05,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  if (isMobile) {
    return (
      <motion.section 
        ref={sectionRef} 
        id="products" 
        className="py-8 bg-white"
        variants={containerVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
      >
        <div className="text-center mb-6 px-6">
          <motion.h2
            className="text-2xl font-playfair text-[#18181B] mb-3"
            variants={titleVariants}
          >
            <span className="font-playfair text-[#18181B]">Explore our </span>
            <span className="font-playfair italic text-[#18181B]">products</span>
          </motion.h2>
          <motion.div
            className="w-16 h-1 bg-[#a98028] mx-auto"
            variants={lineVariants}
          />
        </div>

        {error ? (
          <div className="flex justify-center items-center h-32">
            <p className="text-lg text-red-500">{error}</p>
          </div>
        ) : displayedProducts.length === 0 ? (
          <div className="flex justify-center items-center h-32">
            <div className="inline-flex items-center space-x-2 text-sm text-gray-500">
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-[#a98028]" />
              <span>Loading products...</span>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto h-[48vh] flex flex-col justify-center  px-6" ref={scrollContainerRef}>
            <div className="flex space-x-4" style={{ width: `${displayedProducts.length * 272}px` }}>
              {displayedProducts.map((product, index) => (
                <motion.div
                  key={product._id}
                  custom={index}
                  variants={cardVariants}
                  whileHover={{ 
                    y: -5,
                    transition: { duration: 0.2 }
                  }}
                  className="flex-shrink-0 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden hover:shadow-md"
                  style={{ width: '256px' }}
                >
                  <Link
                    to={`/product/${product.slug || product._id}`}
                    className="block"
                  >
                    <div className="aspect-[4/3] overflow-hidden">
                      <motion.img
                        src={getProductImage(product)}
                        alt={product.name}
                        className="w-full h-full object-cover"
                        onError={onImgError}
                        variants={imageVariants}
                        whileHover="hover"
                      />
                    </div>

                    <div className="p-4">
                      <h3 className="text-lg font-playfair text-[#18181B] mb-2 line-clamp-2">{product.name}</h3>
                      <p className="text-sm font-satoshi text-[#323030] mb-3 line-clamp-1">
                        {product.description || 'No description available'}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-satoshi font-semibold">{formatPrice(product)}</span>
                        <div className="flex items-center text-sm text-gray-500">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        <div className="text-center mt-4 px-6">
          <p className="text-sm text-gray-500 font-satoshi">← Swipe to explore more products →</p>
        </div>
      </motion.section>
    );
  }

  // Desktop
  return (
    <motion.section 
      ref={sectionRef} 
      id="products" 
      className="bg-white px-[20px] sm:px-0 md:px-0 md:py-12 lg:px-[100px]"
      variants={containerVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
    >
      <div className="mx-auto max-w-[1200px]">
        <div className="text-center mb-8">
          <motion.h2
            className="text-[32px] lg:text-[52px] font-playfair text-[#18181B] mb-3"
            variants={titleVariants}
          >
            <span className="text-[32px] lg:text-[52px] font-playfair text-[#18181B]">Explore our </span>
            <span className="text-[32px] lg:text-[52px] font-playfair italic text-[#18181B]">products</span>
          </motion.h2>
          <motion.div
            className="w-16 h-1 bg-[#a98028] mx-auto"
            variants={lineVariants}
          />
        </div>

        {error ? (
          <div className="flex justify-center items-center h-64">
            <p className="text-xl text-red-500">{error}</p>
          </div>
        ) : displayedProducts.length === 0 ? (
          <div className="flex justify-center items-center h-64">
            <div className="inline-flex items-center space-x-3 text-lg text-gray-600">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#a98028]" />
              <span>Loading products...</span>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
            {displayedProducts.map((product, index) => (
              <motion.div
                key={product._id}
                custom={index}
                variants={cardVariants}
                whileHover={{ 
                  y: -10,
                  transition: { duration: 0.3 }
                }}
                className="group"
              >
                <Link
                  to={`/product/${product.slug || product._id}`}
                  className="block"
                >
                  <div className="aspect-[1/1] overflow-hidden mb-5">
                    <motion.img
                      src={getProductImage(product)}
                      alt={product.name}
                      className="w-full h-full object-cover"
                      onError={onImgError}
                      variants={imageVariants}
                      whileHover="hover"
                    />
                  </div>

                  <div className="text-center">
                    <h3 className="text-[22px] lg:text-[26px] font-playfair text-[#18181B] mb-1">{product.name}</h3>
                    <p className="text-[14px] lg:text-[16px] font-satoshi text-[#323030] mb-2 line-clamp-1">
                      {product.description || 'No description available'}
                    </p>
                    <span className="text-[16px] lg:text-[18px] font-satoshi font-medium">
                      {formatPrice(product)}
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </motion.section>
  );
};

export default ProductShowcase;
